import React from 'react'
import PropTypes from 'prop-types'
import { useFormContext, useFieldArray } from 'react-hook-form'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import Typography from '@material-ui/core/Typography'
import AddIcon from 'components/material-symbol/icons/Add'
import RemoveIcon from 'components/material-symbol/icons/Remove'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import { EmailFieldWithSuggestions, PhoneField } from 'components/form-util'
import { stripPhoneNumber } from 'utils/phoneNumber'
import { useAuth } from 'lib/auth'
import InviteLockableTextField from '../../invite-lockable-text-field'
import { useRegistrationInvite } from '../../registration-invite'
import { consumerProfile } from '../../utils/consumer-participant'
import useStyles from './styles'

const CounterRow = ({
  label,
  caption,
  count,
  onAdd,
  onRemove,
  addDisabled,
}) => {
  const classes = useStyles()

  return (
    <div className={classes.counterRow}>
      <div>
        <div className={classes.counterLabel}>{label}</div>
        <div className={classes.counterCaption}>{caption}</div>
      </div>
      <div className={classes.counterControls}>
        <IconButton
          className={classes.counterButton}
          onClick={onRemove}
          aria-label={`Remove ${label.toLowerCase()} participant`}
        >
          <RemoveIcon />
        </IconButton>
        <span className={classes.counterValue} aria-live="polite">
          {count}
        </span>
        <IconButton
          className={classes.counterButton}
          onClick={onAdd}
          disabled={addDisabled}
          aria-label={`Add ${label.toLowerCase()} participant`}
        >
          <AddIcon />
        </IconButton>
      </div>
    </div>
  )
}

CounterRow.propTypes = {
  label: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  addDisabled: PropTypes.bool,
}

const ParticipantFields = ({ tags }) => {
  const classes = useStyles()
  const { register, errors, control, watch } = useFormContext()
  const { user, isConsumer } = useAuth()
  const isAuthenticatedConsumer = user && isConsumer
  const {
    isValid: inviteIsValid,
    firstName: inviteFirstName,
    lastName: inviteLastName,
  } = useRegistrationInvite()
  const useFirstParticipantContactInformation = watch(
    'useFirstParticipantContactInformation',
  )
  const { fields, append, remove } = useFieldArray({
    name: 'participants',
    control,
  })

  const appendParticipantWithType = type => {
    const isFirstAdult =
      type === 'Adult' && !fields.some(p => p.type === 'Adult')
    append(
      isFirstAdult && isAuthenticatedConsumer
        ? { ...consumerProfile(user), type }
        : { type },
      false,
    )
  }
  const removeParticipantWithType = type => {
    const lastParticipantOfTypeIndex = fields.map(p => p.type).lastIndexOf(type)
    if (lastParticipantOfTypeIndex >= 0) {
      remove(lastParticipantOfTypeIndex)
    }
  }

  const isAdultsOnly = tags?.includes('Adults Only')
  const isJuniorsOnly = tags?.includes('Juniors Only')
  const isAgeSpecificGroupEvent = isAdultsOnly || isJuniorsOnly

  const adultParticipantCount = fields.filter(p => p.type === 'Adult').length
  const juniorParticipantCount = fields.filter(p => p.type === 'Junior').length

  return (
    <section className={classes.section}>
      <Typography variant="h6" component="h2" className={classes.sectionTitle}>
        Participants
      </Typography>
      <div className={classes.counterList}>
        {(!isAgeSpecificGroupEvent || isAdultsOnly) && (
          <CounterRow
            label="Adults"
            caption="Ages 18 or above"
            count={adultParticipantCount}
            onAdd={() => appendParticipantWithType('Adult')}
            onRemove={() => removeParticipantWithType('Adult')}
            addDisabled={inviteIsValid}
          />
        )}
        {(!isAgeSpecificGroupEvent || isJuniorsOnly) && (
          <CounterRow
            label="Juniors"
            caption="Ages 17 or under"
            count={juniorParticipantCount}
            onAdd={() => appendParticipantWithType('Junior')}
            onRemove={() => removeParticipantWithType('Junior')}
            addDisabled={inviteIsValid}
          />
        )}
      </div>

      {fields.map((participant, index) => {
        const isContactFromFirstParticipant =
          useFirstParticipantContactInformation && index === 0
        const isInvitedParticipant =
          inviteIsValid && index === 0 && participant.type === 'Adult'

        return (
          <div key={participant.id} className={classes.participantBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.participantTitle}
            >
              Participant #{index + 1} ({participant.type})
            </Typography>
            <div className={classes.fieldGrid}>
              <InviteLockableTextField
                id={`participant-${index}-first-name`}
                name={`participants[${index}].firstName`}
                locked={isInvitedParticipant}
                lockedValue={inviteFirstName}
                error={!!errors?.participants?.[index]?.firstName}
                helperText={errors?.participants?.[index]?.firstName?.message}
                defaultValue={participant.firstName}
                label="First name"
                required
              />
              <InviteLockableTextField
                id={`participant-${index}-last-name`}
                name={`participants[${index}].lastName`}
                locked={isInvitedParticipant}
                lockedValue={inviteLastName}
                error={!!errors?.participants?.[index]?.lastName}
                helperText={errors?.participants?.[index]?.lastName?.message}
                defaultValue={participant.lastName}
                label="Last name"
                required
              />
              {participant.type === 'Adult' && (
                <>
                  <EmailFieldWithSuggestions
                    name={`participants[${index}].email`}
                    control={control}
                    errors={errors}
                    label="Email"
                    classes={{}}
                    defaultValue={participant.email}
                    disabled={isInvitedParticipant}
                    fullWidth
                    required={isContactFromFirstParticipant}
                  />
                  <PhoneField
                    id={`participant-${index}-phone`}
                    name={`participants[${index}].phoneNumber`}
                    inputRef={register()}
                    error={!!errors?.participants?.[index]?.phoneNumber}
                    helperText={
                      errors?.participants?.[index]?.phoneNumber?.message
                    }
                    defaultValue={stripPhoneNumber(participant.phoneNumber)}
                    label="Phone number"
                    fullWidth
                    required={isContactFromFirstParticipant}
                  />
                </>
              )}
            </div>
            <Input
              type="hidden"
              name={`participants[${index}].type`}
              inputRef={register()}
              defaultValue={participant.type}
            />
          </div>
        )
      })}
      {fields.length === 0 && errors?.participants?.message && (
        <div className={classes.inlineError} role="alert">
          <ErrorOutlineIcon />
          <span>Please register at least one participant</span>
        </div>
      )}
    </section>
  )
}

ParticipantFields.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string),
}

export default ParticipantFields
